<template>
    <a-modal
        :visible="true"
        :title="$_.get(block, 'name', $t('chatbotv2.blockDetails'))"
        :width="800"
        @cancel="$emit('close')"
        :footer="null"
    >
        <div class="block-details-content" v-if="block && !loading">
            <div class="block-header-section">
                <span class="material-icons block-icon-large">{{ block.icon || "extension" }}</span>
                <div class="header-info">
                    <h2>{{ block.name }}</h2>
                    <p class="description">{{ block.description }}</p>
                    <div class="meta-tags">
                        <a-tag color="blue">{{ block.category }}</a-tag>
                        <a-tag v-for="tag in block.tags" :key="tag">{{ tag }}</a-tag>
                    </div>
                </div>
            </div>

            <a-divider />

            <div class="stats-section">
                <div class="stat-item">
                    <a-icon type="download" />
                    <span>{{ block.usageCount || 0 }} {{ $t("chatbotv2.uses") }}</span>
                </div>
                <div class="stat-item">
                    <a-icon type="star" theme="filled" style="color: #faad14" />
                    <span>{{ block.averageRating ? block.averageRating.toFixed(1) : "0.0" }} ({{ block.totalRatings || 0 }} {{ $t("chatbotv2.ratings") }})</span>
                </div>
            </div>

            <a-divider />

            <div class="parameters-section">
                <h3>{{ $t("chatbotv2.parameters") }}</h3>
                <div class="parameters-list" v-if="block.parameters && block.parameters.length > 0">
                    <div class="parameter-item" v-for="param in block.parameters" :key="param.name">
                        <div class="param-header">
                            <strong>{{ param.label }}</strong>
                            <a-tag size="small" color="blue">{{ param.type }}</a-tag>
                            <a-tag v-if="param.required" size="small" color="red">{{ $t("chatbotv2.isRequired") }}</a-tag>
                        </div>
                        <p class="param-description" v-if="param.description">{{ param.description }}</p>
                        <p class="param-default" v-if="param.defaultValue">
                            {{ $t("auto.blockEditorDefaultLabel") }} <code>{{ param.defaultValue }}</code>
                        </p>
                    </div>
                </div>
                <p v-else class="no-parameters">{{ $t("chatbotv2.noParameters") }}</p>
            </div>

            <a-divider />

            <div class="ratings-section">
                <h3>{{ $t("chatbotv2.ratings") }}</h3>
                
                <!-- User's rating form -->
                <div class="add-rating" v-if="!userRating">
                    <a-rate v-model="newRating" />
                    <a-textarea
                        v-model="newComment"
                        :rows="3"
                        :placeholder="$t('chatbotv2.writeReview')"
                    />
                    <a-button type="primary" size="small" @click="submitRating" :loading="submittingRating">
                        {{ $t("chatbotv2.submitRating") }}
                    </a-button>
                </div>

                <!-- Display ratings -->
                <div class="ratings-list" v-if="ratings.length > 0">
                    <div class="rating-item" v-for="rating in ratings" :key="rating._id">
                        <div class="rating-header">
                            <a-rate :value="rating.rating" disabled />
                            <span class="rating-date">{{ formatDate(rating.createdAt) }}</span>
                        </div>
                        <p class="rating-comment" v-if="rating.comment">{{ rating.comment }}</p>
                    </div>
                </div>
                <p v-else class="no-ratings">{{ $t("chatbotv2.noRatings") }}</p>
            </div>

            <div class="modal-footer">
                <a-button @click="$emit('close')">{{ $t("message.close") }}</a-button>
                <a-button
                    v-if="!isImported"
                    type="primary"
                    @click="handleImport"
                    :loading="importing"
                >
                    <a-icon type="download" />
                    {{ $t("chatbotv2.import") }}
                </a-button>
                <a-tag v-else color="green">
                    {{ $t("chatbotv2.imported") }}
                </a-tag>
            </div>
        </div>

        <div class="loading-container" v-else>
            <a-spin size="large" />
        </div>
    </a-modal>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";
import { CUSTOM_BLOCKS_API } from "../../../../../api/chatbotCustomBlocks";

export default {
    name: "BlockDetailsModal",
    props: {
        blockId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            block: null,
            ratings: [],
            loading: true,
            importing: false,
            newRating: 0,
            newComment: "",
            submittingRating: false,
            userRating: null,
        };
    },
    computed: {
        ...mapGetters({
            isImported: "customBlocks/isImported",
        }),
    },
    methods: {
        ...mapActions({
            getBlockDetails: "customBlocks/getBlockDetails",
            importBlock: "customBlocks/importBlock",
        }),
        async loadBlock() {
            this.loading = true;
            try {
                const { data: blockData } = await CUSTOM_BLOCKS_API.getDetails(this.blockId, false);
                this.block = blockData;

                const { data: ratingsData } = await CUSTOM_BLOCKS_API.getRatings(this.blockId);
                this.ratings = ratingsData;
            } catch (error) {
                this.$message.error(error.message || "Failed to load block details");
            } finally {
                this.loading = false;
            }
        },
        async handleImport() {
            this.importing = true;
            try {
                await this.importBlock({ id: this.blockId, versionId: null });
                this.$message.success(this.$t("chatbotv2.blockImported") || "Block imported successfully");
                this.$emit("import");
            } catch (error) {
                var errorCode = (error.response && error.response.data && error.response.data.error) || "";
                var translationKey = "customBlockErrors." + errorCode;
                var translated = this.$t(translationKey);
                this.$message.error(translated !== translationKey ? translated : this.$t("customBlockErrors.BLOCK_IMPORT_FAILED"));
            } finally {
                this.importing = false;
            }
        },
        async submitRating() {
            if (this.newRating === 0) {
                this.$message.warning(this.$t("chatbotv2.pleaseSelectRating") || "Please select a rating");
                return;
            }

            this.submittingRating = true;
            try {
                await CUSTOM_BLOCKS_API.rate(this.blockId, this.newRating, this.newComment);
                this.$message.success(this.$t("chatbotv2.ratingSubmitted") || "Rating submitted");
                this.newRating = 0;
                this.newComment = "";
                await this.loadBlock();
            } catch (error) {
                this.$message.error(error.message || "Failed to submit rating");
            } finally {
                this.submittingRating = false;
            }
        },
        formatDate(date) {
            return moment(date).format("DD/MM/YYYY");
        },
    },
    created() {
        this.loadBlock();
    },
};
</script>

<style lang="scss" scoped>
.block-details-content {
    .block-header-section {
        display: flex;
        gap: 20px;
        margin-bottom: 20px;

        .block-icon-large {
            font-size: 80px;
            color: #9c27b0;
        }

        .header-info {
            flex: 1;

            h2 {
                margin: 0 0 12px 0;
                font-size: 24px;
            }

            .description {
                color: #666;
                margin-bottom: 12px;
            }

            .meta-tags {
                display: flex;
                gap: 8px;
                flex-wrap: wrap;
            }
        }
    }

    .stats-section {
        display: flex;
        gap: 24px;

        .stat-item {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 16px;
        }
    }

    .parameters-section {
        h3 {
            margin-bottom: 16px;
        }

        .parameters-list {
            display: flex;
            flex-direction: column;
            gap: 16px;

            .parameter-item {
                background: #f5f5f5;
                padding: 12px;
                border-radius: 6px;

                .param-header {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin-bottom: 8px;
                }

                .param-description {
                    margin: 0 0 4px 0;
                    color: #666;
                    font-size: 13px;
                }

                .param-default {
                    margin: 0;
                    font-size: 12px;
                    color: #999;

                    code {
                        background: #fff;
                        padding: 2px 6px;
                        border-radius: 3px;
                    }
                }
            }
        }

        .no-parameters {
            color: #999;
            font-style: italic;
        }
    }

    .ratings-section {
        h3 {
            margin-bottom: 16px;
        }

        .add-rating {
            background: #f5f5f5;
            padding: 16px;
            border-radius: 8px;
            margin-bottom: 20px;
            display: flex;
            flex-direction: column;
            gap: 12px;
        }

        .ratings-list {
            display: flex;
            flex-direction: column;
            gap: 12px;
            max-height: 300px;
            overflow-y: auto;

            .rating-item {
                padding: 12px;
                border: 1px solid #e0e0e0;
                border-radius: 6px;

                .rating-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 8px;

                    .rating-date {
                        font-size: 12px;
                        color: #999;
                    }
                }

                .rating-comment {
                    margin: 0;
                    color: #666;
                    font-size: 14px;
                }
            }
        }

        .no-ratings {
            color: #999;
            font-style: italic;
            text-align: center;
        }
    }

    .modal-footer {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
        margin-top: 24px;
        padding-top: 16px;
        border-top: 1px solid #e0e0e0;
    }
}

.loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
}
</style>

